import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { montrealPins } from '../data/pins';
import { parseMetadata } from '../utils/metadataParser';
import TableOfContents from './TableOfContents';

const LocationPage = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const [markdownContent, setMarkdownContent] = useState('');
  const [showTOC, setShowTOC] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const highlightTimeoutRef = React.useRef(null);

  const handleBackClick = () => {
    // Check if there's history to go back to
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // Fallback to homepage if no history
      navigate('/');
    }
  };

  const location = montrealPins.find(pin => pin.id === locationId);

  useEffect(() => {
    if (!location) {
      setError('Location not found');
      setLoading(false);
      return;
    }

    const fetchMarkdown = async () => {
      try {
        const response = await fetch(`/content/${location.markdownFile}`);
        if (!response.ok) {
          throw new Error('Failed to load content');
        }
        const content = await response.text();
        
        // Parse metadata and get content without metadata section
        const { contentWithoutMetadata } = parseMetadata(content);
        setMarkdownContent(contentWithoutMetadata);
      } catch (err) {
        setError('Failed to load location content');
      } finally {
        setLoading(false);
      }
    };

    fetchMarkdown();
  }, [location]);

  // Check screen width for TOC display
  useEffect(() => {
    const checkScreenWidth = () => {
      setShowTOC(window.innerWidth >= 1000); // Show TOC on wider screens
    };

    checkScreenWidth();
    window.addEventListener('resize', checkScreenWidth);

    return () => window.removeEventListener('resize', checkScreenWidth);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  // Handle citation clicks via event delegation
  const handleContentClick = (e) => {
    if (e.target.classList.contains('citation-link')) {
      e.preventDefault();
      const href = e.target.getAttribute('href');
      const targetId = href.substring(1); // Remove the # from href
      let referenceElement = document.getElementById(targetId);
      
      // Fallback: if ID not found, search for text content containing [number]
      if (!referenceElement) {
        const refNumber = targetId.replace('ref-', '');
        const allElements = document.querySelectorAll('p, li, div');
        for (const element of allElements) {
          if (element.textContent.trim().startsWith(`[${refNumber}]`)) {
            referenceElement = element;
            // Add the ID for future clicks
            element.id = targetId;
            break;
          }
        }
      }
      
      if (referenceElement) {
        referenceElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        // Highlight the reference briefly
        referenceElement.style.backgroundColor = '#ffeb3b';

        // Clear previous timeout if exists
        if (highlightTimeoutRef.current) {
          clearTimeout(highlightTimeoutRef.current);
        }

        highlightTimeoutRef.current = setTimeout(() => {
          referenceElement.style.backgroundColor = '';
          highlightTimeoutRef.current = null;
        }, 2000);
      } else {
        console.log(`Reference ${targetId} not found`);
      }
    }
  };

  // Preprocess markdown to convert citations to HTML
  const preprocessMarkdown = (markdown) => {
    if (!markdown) return markdown;
    
    let processed = markdown;
    
    // First, add IDs to reference lines (lines that start with [number])
    processed = processed.replace(/^(\[(\d+)\].*)$/gm, (match, fullLine, refNumber) => {
      return `<div id="ref-${refNumber}">${fullLine}</div>`;
    });
    
    // Then convert citation links to superscript
    processed = processed.replace(/\[(\d+(?:,\s*\d+)*)\]/g, (match, numbers, offset, string) => {
      // Don't convert if this is already in a reference line (starts with [number] at beginning of line)
      const beforeMatch = string.substring(0, offset);
      const lineStart = beforeMatch.lastIndexOf('\n') + 1;
      const currentLine = string.substring(lineStart, offset + match.length);
      
      // Skip if this is a reference definition (line starts with [number])
      if (/^(\[(\d+)\])/.test(currentLine.trim())) {
        return match; // Return unchanged
      }
      
      const numArray = numbers.split(',').map(n => n.trim());
      const links = numArray.map(num => 
        `<a href="#ref-${num}" class="citation-link">${num}</a>`
      ).join(',');
      return `<sup class="citation">${links}</sup>`;
    });
    
    return processed;
  };

  if (loading) {
    return (
      <div className="location-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error || !location) {
    return (
      <div className="location-page">
        <div className="error">
          <h2>Error</h2>
          <p>{error || 'Location not found'}</p>
          <button onClick={() => navigate('/')}>Back to Map</button>
        </div>
      </div>
    );
  }

  // Custom renderer to add IDs to headings, handle image captions, and parse citations
  const components = {
    h1: ({children, ...props}) => {
      const id = children.toString()
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .trim();
      return <h1 id={id} {...props}>{children}</h1>;
    },
    h2: ({children, ...props}) => {
      const id = children.toString()
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .trim();
      return <h2 id={id} {...props}>{children}</h2>;
    },
    h3: ({children, ...props}) => {
      const id = children.toString()
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .trim();
      return <h3 id={id} {...props}>{children}</h3>;
    },
    img: ({alt, src, title, ...props}) => {
      // Use title as positioning class instead of caption
      const positionClass = title ? `image-position-${title}` : '';
      return <img src={src} alt={alt} className={positionClass} {...props} />;
    },
    // Add IDs to list items in reference sections
    li: ({children, ...props}) => {
      // Check if this is likely a reference (starts with [number])
      const childText = React.Children.toArray(children).join('');
      const refMatch = childText.match(/^\[(\d+)\]/);
      if (refMatch) {
        const refNum = refMatch[1];
        return <li id={`ref-${refNum}`} {...props}>{children}</li>;
      }
      return <li {...props}>{children}</li>;
    }
  };

  return (
    <div className={`location-page fade-in ${showTOC ? 'with-toc' : ''}`}>
      {!showTOC && (
        <header className="location-header fade-in">
          <button className="back-button" onClick={handleBackClick}>
            ← Back
          </button>
        </header>
      )}
      
      <div className="location-body">
        {showTOC && (
          <div className="left-sidebar">
            <header className="location-header fade-in">
              <button className="back-button" onClick={handleBackClick}>
                ← Back
              </button>
            </header>
            <TableOfContents markdownContent={markdownContent} />
          </div>
        )}
        
        <main className="location-content fade-in" onClick={handleContentClick}>
          <ReactMarkdown 
            components={components}
            rehypePlugins={[rehypeRaw]}
          >
            {preprocessMarkdown(markdownContent)}
          </ReactMarkdown>
        </main>
      </div>
    </div>
  );
};

export default LocationPage;